<template>
  <div
    v-if="isVisible"
    class="reference-search-modal-overlay"
    @click.self="close"
    @keydown.esc="close"
  >
    <div class="reference-search-modal" @click.stop>
      <div class="reference-search-modal-header">
        <h5 class="reference-search-modal-title">
          Buscar {{ fieldLabel }}
        </h5>
        <button
          type="button"
          class="btn-close"
          @click="close"
          aria-label="Cerrar"
        ></button>
      </div>
      <div class="reference-search-modal-body">
        <!-- Input de búsqueda grande -->
        <div class="reference-search-input-container">
          <input
            ref="searchInput"
            type="text"
            class="form-control form-control-lg reference-search-input"
            :placeholder="searchPlaceholder"
            v-model="searchTerm"
            @input="onSearchInput"
            @keydown.enter.prevent="selectFirstResult"
            @keydown.esc="close"
            autofocus
          />
          <div v-if="isSearching" class="reference-search-loading">
            <div class="spinner-border spinner-border-sm text-primary" role="status">
              <span class="visually-hidden">Buscando...</span>
            </div>
          </div>
        </div>

        <!-- Resultados de búsqueda -->
        <div class="reference-search-results">
          <div v-if="!searchTerm || searchTerm.trim().length < 2" class="reference-search-empty">
            <p class="text-muted">Escribe al menos 2 caracteres para buscar</p>
          </div>
          <div v-else-if="isSearching && filteredOptions.length === 0" class="reference-search-empty">
            <p class="text-muted">Buscando...</p>
          </div>
          <div v-else-if="filteredOptions.length === 0" class="reference-search-empty">
            <p class="text-muted">No se encontraron resultados</p>
          </div>
          <div v-else class="reference-search-list">
            <div
              v-for="(option, index) in filteredOptions"
              :key="option._id || index"
              class="reference-search-item"
              :class="{ 'selected': selectedIndex === index }"
              @click="selectOption(option)"
              @mouseenter="selectedIndex = index"
            >
              <div class="reference-search-item-name">
                {{ getOptionLabel(option) }}
              </div>
              <div v-if="option.description" class="reference-search-item-description">
                {{ option.description }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="reference-search-modal-footer">
        <button type="button" class="btn btn-secondary btn-sm" @click="close">
          Cancelar
        </button>
        <small class="text-muted ms-2">Presiona Enter para seleccionar el primer resultado</small>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";

export default {
  name: "ReferenceSearchModal",
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    field: {
      type: Object,
      required: true,
    },
    currentValue: {
      type: [Object, Array, String, null],
      default: null,
    },
    referenceOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      searchTerm: "",
      filteredOptions: [],
      isSearching: false,
      searchTimeout: null,
      selectedIndex: -1,
      allOptions: [],
    };
  },
  computed: {
    fieldLabel() {
      return this.field?.label || "referencia";
    },
    searchPlaceholder() {
      if (this.field?.preload) {
        return this.field.isArray
          ? "Buscar y seleccionar una o más opciones..."
          : "Buscar y seleccionar una opción...";
      }
      return this.field.isArray
        ? "Escribe para buscar registros..."
        : "Escribe para buscar un registro...";
    },
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus();
          }
          this.initializeOptions();
        });
      } else {
        this.searchTerm = "";
        this.filteredOptions = [];
        this.selectedIndex = -1;
      }
    },
    referenceOptions: {
      handler(newOptions) {
        if (this.field?.preload) {
          this.allOptions = [...newOptions];
          this.filterOptions();
        }
      },
      immediate: true,
    },
  },
  methods: {
    initializeOptions() {
      if (this.field?.preload) {
        this.allOptions = [...this.referenceOptions];
        this.filterOptions();
      } else {
        if (this.currentValue) {
          if (this.field.isArray && Array.isArray(this.currentValue)) {
            this.allOptions = this.currentValue.map((item) =>
              typeof item === "string" ? { _id: item, name: item } : item
            );
          } else if (this.currentValue) {
            this.allOptions = [
              typeof this.currentValue === "string"
                ? { _id: this.currentValue, name: this.currentValue }
                : this.currentValue,
            ];
          }
        }
      }
    },
    onSearchInput() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      if (!this.searchTerm || this.searchTerm.trim().length < 2) {
        this.filteredOptions = [];
        this.selectedIndex = -1;
        return;
      }

      if (this.field?.preload) {
        this.filterOptions();
      } else {
        this.searchTimeout = setTimeout(() => {
          this.searchInServer();
        }, 300);
      }
    },
    filterOptions() {
      if (!this.searchTerm || this.searchTerm.trim().length < 2) {
        this.filteredOptions = [];
        return;
      }

      // Búsqueda por palabras clave en todos los campos de texto
      const term = this.searchTerm.toLowerCase().trim();
      this.filteredOptions = this.allOptions.filter((option) => {
        // Buscar en name
        if (option.name && option.name.toLowerCase().includes(term)) {
          return true;
        }
        // Buscar en label
        if (option.label && option.label.toLowerCase().includes(term)) {
          return true;
        }
        // Buscar en textPlainVersion si existe (búsqueda por palabras clave)
        if (option.textPlainVersion && option.textPlainVersion.toLowerCase().includes(term)) {
          return true;
        }
        // Buscar en otros campos de texto del objeto
        for (const key in option) {
          if (key !== '_id' && key !== '__v' && key !== 'createdAt' && key !== 'updatedAt' && 
              key !== 'createdBy' && key !== 'updatedBy' && key !== 'deleted' && key !== 'editHistory') {
            const value = option[key];
            if (typeof value === 'string' && value.toLowerCase().includes(term)) {
              return true;
            }
          }
        }
        return false;
      });

      if (this.currentValue) {
        const currentId =
          typeof this.currentValue === "string"
            ? this.currentValue
            : this.currentValue._id || this.currentValue;
        const exists = this.filteredOptions.some((opt) => opt._id === currentId);
        if (!exists && currentId) {
          if (this.field.isArray && Array.isArray(this.currentValue)) {
            this.currentValue.forEach((item) => {
              const itemId = typeof item === "string" ? item : item._id || item;
              const itemExists = this.filteredOptions.some(
                (opt) => opt._id === itemId
              );
              if (!itemExists && itemId) {
                this.filteredOptions.push(
                  typeof item === "string" ? { _id: item, name: item } : item
                );
              }
            });
          } else {
            this.filteredOptions.push(
              typeof this.currentValue === "string"
                ? { _id: this.currentValue, name: this.currentValue }
                : this.currentValue
            );
          }
        }
      }

      this.selectedIndex = -1;
    },
    async searchInServer() {
      if (!this.field?.ref) {
        return;
      }

      this.isSearching = true;

      try {
        const params = new URLSearchParams();
        params.append("textPlainVersion", this.searchTerm.trim());
        params.append("perPage", "50");

        if (this.field.refFilter && this.field.refFilterValue) {
          params.append(this.field.refFilter, this.field.refFilterValue);
        }

        const url = `/api/gotrading/dynamic/${this.field.ref}?${params.toString()}`;
        const { data } = await wencesApi.get(url);

        const validData = (data.items || data || []).filter(
          (item) => item && (item._id || item.name)
        );

        this.allOptions = validData;
        this.filteredOptions = validData;

        if (this.currentValue) {
          const currentId =
            typeof this.currentValue === "string"
              ? this.currentValue
              : this.currentValue._id || this.currentValue;
          const exists = this.filteredOptions.some((opt) => opt._id === currentId);
          if (!exists && currentId) {
            this.filteredOptions.push(
              typeof this.currentValue === "string"
                ? { _id: this.currentValue, name: this.currentValue }
                : this.currentValue
            );
          }
        }
      } catch (error) {
        console.error("Error searching references:", error);
        this.filteredOptions = [];
      } finally {
        this.isSearching = false;
      }
    },
    selectOption(option) {
      // Asegurarse de que el option tenga name/label antes de emitir
      if (!option) {
        return;
      }
      
      // Si el option no tiene name ni label, intentar obtenerlo de allOptions
      if (!option.name && !option.label && option._id) {
        const fullOption = this.allOptions.find(opt => opt._id === option._id);
        if (fullOption && (fullOption.name || fullOption.label)) {
          option = fullOption;
        } else if (!option.name && !option.label) {
          // Si aún no tiene name, usar el _id como name temporal
          option = { ...option, name: option._id };
        }
      }
      
      if (this.field.isArray) {
        const current = Array.isArray(this.currentValue) ? [...this.currentValue] : [];
        const exists = current.some(
          (item) =>
            (typeof item === "string" ? item : item._id) === option._id
        );
        if (exists) {
          const newValue = current.filter(
            (item) =>
              (typeof item === "string" ? item : item._id) !== option._id
          );
          this.$emit("select", newValue);
        } else {
          this.$emit("select", [...current, option]);
        }
      } else {
        this.$emit("select", option);
        this.close();
      }
    },
    selectFirstResult() {
      if (this.filteredOptions.length > 0) {
        this.selectOption(this.filteredOptions[0]);
      }
    },
    getOptionLabel(option) {
      if (!option) return "No name";
      return option.name || option.label || option._id || "No name";
    },
    close() {
      this.$emit("close");
    },
  },
  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
};
</script>

<style scoped>
.reference-search-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100000;
  padding: 20px;
}

.reference-search-modal {
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.reference-search-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem;
  border-bottom: 1px solid #dee2e6;
}

.reference-search-modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.reference-search-modal-body {
  padding: 1.25rem;
  overflow-y: auto;
  flex: 1;
}

.reference-search-input-container {
  position: relative;
  margin-bottom: 1.5rem;
}

.reference-search-input {
  font-size: 1.1rem !important;
  padding: 12px 16px !important;
  border: 2px solid #dee2e6;
  border-radius: 6px;
  width: 100%;
  transition: border-color 0.2s;
}

.reference-search-input:focus {
  border-color: var(--bs-primary);
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(17, 181, 201, 0.25);
}

.reference-search-loading {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
}

.reference-search-results {
  max-height: 400px;
  overflow-y: auto;
}

.reference-search-empty {
  text-align: center;
  padding: 2rem;
  color: #6c757d;
}

.reference-search-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.reference-search-item {
  padding: 12px 16px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
}

.reference-search-item:hover,
.reference-search-item.selected {
  background-color: #f8f9fa;
  border-color: var(--bs-primary);
  transform: translateX(4px);
}

.reference-search-item-name {
  font-size: 1rem;
  font-weight: 500;
  color: #212529;
  margin-bottom: 4px;
}

.reference-search-item-description {
  font-size: 0.875rem;
  color: #6c757d;
}

.reference-search-modal-footer {
  padding: 1rem 1.25rem;
  border-top: 1px solid #dee2e6;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>

<style>
/* Estilos dark mode para el modal de búsqueda */
#darkdesign .reference-search-modal-overlay {
  background-color: rgba(0, 0, 0, 0.7) !important;
}

#darkdesign .reference-search-modal {
  background: var(--dark-bg-secondary) !important;
  color: var(--dark-text-primary) !important;
}

#darkdesign .reference-search-modal-header {
  border-bottom-color: var(--dark-border-color) !important;
}

#darkdesign .reference-search-modal-title {
  color: var(--dark-text-primary) !important;
}

#darkdesign .reference-search-modal-body {
  background: var(--dark-bg-secondary) !important;
}

#darkdesign .reference-search-input {
  background-color: var(--dark-bg-input) !important;
  color: var(--dark-text-primary) !important;
  border-color: var(--dark-border-color) !important;
}

#darkdesign .reference-search-input:focus {
  background-color: var(--dark-bg-input) !important;
  color: var(--dark-text-primary) !important;
  border-color: var(--bs-primary) !important;
  box-shadow: 0 0 0 0.2rem rgba(17, 181, 201, 0.25) !important;
}

#darkdesign .reference-search-input::placeholder {
  color: var(--dark-text-muted) !important;
}

#darkdesign .reference-search-item {
  background: var(--dark-bg-tertiary) !important;
  border-color: var(--dark-border-color) !important;
  color: var(--dark-text-primary) !important;
}

#darkdesign .reference-search-item:hover,
#darkdesign .reference-search-item.selected {
  background-color: var(--dark-bg-hover) !important;
  border-color: var(--bs-primary) !important;
}

#darkdesign .reference-search-item-name {
  color: var(--dark-text-primary) !important;
}

#darkdesign .reference-search-item-description {
  color: var(--dark-text-muted) !important;
}

#darkdesign .reference-search-empty {
  color: var(--dark-text-muted) !important;
}

#darkdesign .reference-search-modal-footer {
  border-top-color: var(--dark-border-color) !important;
}

#darkdesign .reference-search-modal-footer .btn-secondary {
  background-color: var(--dark-bg-tertiary) !important;
  border-color: var(--dark-border-color) !important;
  color: var(--dark-text-primary) !important;
}

#darkdesign .reference-search-modal-footer .btn-secondary:hover {
  background-color: var(--dark-bg-hover) !important;
}

#darkdesign .reference-search-modal-footer .text-muted {
  color: var(--dark-text-muted) !important;
}

/* Estilos para modo dark usando html.dark-mode */
html.dark-mode .reference-search-modal-overlay {
  background-color: rgba(0, 0, 0, 0.7) !important;
}

html.dark-mode .reference-search-modal {
  background: var(--dark-bg-secondary) !important;
  color: var(--dark-text-primary) !important;
}

html.dark-mode .reference-search-modal-header {
  border-bottom-color: var(--dark-border-color) !important;
}

html.dark-mode .reference-search-modal-title {
  color: var(--dark-text-primary) !important;
}

html.dark-mode .reference-search-modal-body {
  background: var(--dark-bg-secondary) !important;
}

html.dark-mode .reference-search-input {
  background-color: var(--dark-bg-input) !important;
  color: var(--dark-text-primary) !important;
  border-color: var(--dark-border-color) !important;
}

html.dark-mode .reference-search-input:focus {
  background-color: var(--dark-bg-input) !important;
  color: var(--dark-text-primary) !important;
  border-color: var(--bs-primary) !important;
  box-shadow: 0 0 0 0.2rem rgba(17, 181, 201, 0.25) !important;
}

html.dark-mode .reference-search-input::placeholder {
  color: var(--dark-text-muted) !important;
}

html.dark-mode .reference-search-item {
  background: var(--dark-bg-tertiary) !important;
  border-color: var(--dark-border-color) !important;
  color: var(--dark-text-primary) !important;
}

html.dark-mode .reference-search-item:hover,
html.dark-mode .reference-search-item.selected {
  background-color: var(--dark-bg-hover) !important;
  border-color: var(--bs-primary) !important;
}

html.dark-mode .reference-search-item-name {
  color: var(--dark-text-primary) !important;
}

html.dark-mode .reference-search-item-description {
  color: var(--dark-text-muted) !important;
}

html.dark-mode .reference-search-empty {
  color: var(--dark-text-muted) !important;
}

html.dark-mode .reference-search-modal-footer {
  border-top-color: var(--dark-border-color) !important;
}

html.dark-mode .reference-search-modal-footer .btn-secondary {
  background-color: var(--dark-bg-tertiary) !important;
  border-color: var(--dark-border-color) !important;
  color: var(--dark-text-primary) !important;
}

html.dark-mode .reference-search-modal-footer .btn-secondary:hover {
  background-color: var(--dark-bg-hover) !important;
}

html.dark-mode .reference-search-modal-footer .text-muted {
  color: var(--dark-text-muted) !important;
}
</style>

